import { useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { DeleteOutline, Edit } from "@mui/icons-material";
import ConfirmDialog from "../../atoms/ConfirmDialog/ConfirmDialog";
import SelectField from "../../atoms/SelectField/SelectField";
import { findAllModules } from "../../../API/apiModules";
import {
  createPermissionAction,
  deletePermissionAction,
  findAllPermissionActions,
  updatePermissionAction,
} from "../../../API/apiPermissionActions";
import {
  PermissionAction,
  SavePermissionActionPayload,
} from "../../../types/SystemActionPermission";
import { SystemModule } from "../../../types/SystemModule";
import { useAuth } from "../../../resources/AuthProvider";
import styles from "./GestaoAcoesPermissao.module.css";
import styles_button from "../../../styles/Button.module.css";
import styles_general from "../../../styles/General.module.css";
import styles_grid from "../../../styles/Grid.module.css";
import styles_input from "../../../styles/Input.module.css";
import styles_table from "../../../styles/Table.module.css";

const ACTION_OPTIONS = [
  { value: "view", label: "Visualizar" },
  { value: "create", label: "Cadastrar" },
  { value: "update", label: "Alterar" },
  { value: "delete", label: "Excluir" },
  { value: "export", label: "Exportar" },
  { value: "other", label: "Outro" },
];

const SCOPE_OPTIONS = [
  { value: "all", label: "Todos registros" },
  { value: "own", label: "Meus registros" },
];

const normalizeToken = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, "")
    .trim()
    .replace(/\s+/g, "_");

const normalizeCustomActionToken = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9_\s]/g, "")
    .trim()
    .replace(/\s+/g, "_");

const getActionLabel = (value: string) =>
  ACTION_OPTIONS.find((option) => option.value === value)?.label || "";

const getScopeLabel = (value: string) =>
  SCOPE_OPTIONS.find((option) => option.value === value)?.label || "";

const actionSupportsScope = (action: string) =>
  ["view", "update", "delete", "export", "other"].includes(action);

const buildPermissionKey = (
  moduleName: string,
  action: string,
  scope: string,
  customAction: string,
) => {
  const normalizedModule = normalizeToken(moduleName);
  const normalizedAction =
    action === "other"
      ? normalizeCustomActionToken(customAction)
      : normalizeToken(action);

  if (!normalizedModule || !normalizedAction) return "";

  if (actionSupportsScope(action) && scope) {
    return `${normalizedModule}.${normalizedAction}_${normalizeToken(scope)}`;
  }

  return `${normalizedModule}.${normalizedAction}`;
};

const INITIAL_FORM = {
  moduleName: "",
  key: "",
  action: "",
  customAction: "",
  scope: "",
  label: "",
  description: "",
  active: true,
};

const GestaoAcoesPermissao = () => {
  const { userPageList } = useAuth();
  const canAccessForm = userPageList.includes("acoes-permissao");

  const [permissionActions, setPermissionActions] = useState<PermissionAction[]>([]);
  const [modules, setModules] = useState<SystemModule[]>([]);
  const [moduleFilter, setModuleFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const [msgSuccess, setMsgSuccess] = useState<string | null>(null);
  const [msgError, setMsgError] = useState<string | null>(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [selectedPermissionAction, setSelectedPermissionAction] =
    useState<PermissionAction | null>(null);
  const [form, setForm] = useState(INITIAL_FORM);

  useEffect(() => {
    loadModules();
  }, []);

  useEffect(() => {
    loadPermissionActions();
  }, [moduleFilter, modules.length]);

  useEffect(() => {
    if (!msgSuccess) return;
    const timer = setTimeout(() => setMsgSuccess(null), 5000);
    return () => clearTimeout(timer);
  }, [msgSuccess]);

  useEffect(() => {
    if (!msgError) return;
    const timer = setTimeout(() => setMsgError(null), 5000);
    return () => clearTimeout(timer);
  }, [msgError]);

  const moduleOptions = useMemo(
    () => [{ nome: "Todos" }, ...modules.map((module) => ({ nome: module.name }))],
    [modules],
  );

  const formModuleOptions = useMemo(
    () => modules.map((module) => ({ nome: module.name })),
    [modules],
  );

  const selectedModuleFilter = useMemo(
    () => modules.find((module) => module.name === moduleFilter),
    [moduleFilter, modules],
  );

  const selectedFormModule = useMemo(
    () => modules.find((module) => module.name === form.moduleName),
    [form.moduleName, modules],
  );

  const loadModules = async () => {
    try {
      const response = await findAllModules();
      setModules(response.data || []);
    } catch (error) {
      setMsgError("Não foi possível carregar os módulos.");
    }
  };

  const loadPermissionActions = async () => {
    setLoading(true);
    try {
      const response = await findAllPermissionActions(
        selectedModuleFilter ? { module_id: selectedModuleFilter.id } : undefined,
      );
      setPermissionActions(response.data || []);
    } catch (error) {
      setMsgError("Não foi possível listar as ações.");
    } finally {
      setLoading(false);
    }
  };

  const getModuleName = (permissionAction: PermissionAction) => {
    if (typeof permissionAction.module === "string") return permissionAction.module;
    return permissionAction.module?.name || "-";
  };

  const closeForm = () => {
    setOpenForm(false);
    setSelectedPermissionAction(null);
    setForm(INITIAL_FORM);
  };

  const openCreateForm = () => {
    setSelectedPermissionAction(null);
    setForm(INITIAL_FORM);
    setOpenForm(true);
  };

  const openEditForm = (permissionAction: PermissionAction) => {
    const actionOption = ACTION_OPTIONS.find(
      (option) => option.value === permissionAction.action,
    );

    setSelectedPermissionAction(permissionAction);
    setForm({
      moduleName: getModuleName(permissionAction),
      key: permissionAction.key || "",
      action: actionOption ? permissionAction.action || "" : "other",
      customAction: actionOption ? "" : permissionAction.action || "",
      scope: permissionAction.scope || "",
      label: permissionAction.label || "",
      description: permissionAction.description || "",
      active: Boolean(permissionAction.active),
    });
    setOpenForm(true);
  };

  const handleSave = async () => {
    if (
      !selectedFormModule
      || !form.action.trim()
      || (form.action === "other" && !form.customAction.trim())
      || !form.key.trim()
      || !form.label.trim()
    ) {
      setMsgError("Preencha módulo, ação, key e label.");
      return;
    }

    try {
      setLoadingSave(true);
      const actionValue =
        form.action === "other"
          ? normalizeCustomActionToken(form.customAction)
          : form.action.trim();
      const payload: SavePermissionActionPayload = {
        module_id: selectedFormModule.id,
        key: form.key.trim(),
        action: actionValue,
        scope: actionSupportsScope(form.action) ? form.scope.trim() || null : null,
        label: form.label.trim(),
        description: form.description.trim(),
        active: form.active,
      };

      if (selectedPermissionAction) {
        await updatePermissionAction(selectedPermissionAction.id, payload);
        setMsgSuccess("Ação atualizada com sucesso.");
      } else {
        await createPermissionAction(payload);
        setMsgSuccess("Ação criada com sucesso.");
      }

      closeForm();
      await loadPermissionActions();
    } catch (error) {
      setMsgError("Não foi possível salvar a ação.");
    } finally {
      setLoadingSave(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedPermissionAction) return;

    try {
      setLoadingDelete(true);
      await deletePermissionAction(selectedPermissionAction.id);
      setMsgSuccess("Ação excluída com sucesso.");
      setOpenDelete(false);
      setSelectedPermissionAction(null);
      await loadPermissionActions();
    } catch (error: any) {
      setOpenDelete(false);
      setMsgError(
        error?.response?.data?.message || "Não foi possível excluir a ação.",
      );
    } finally {
      setLoadingDelete(false);
    }
  };

  return (
    <Card className={styles.card}>
      <CardContent>
        <div className={styles.title}>Gestão de Ações</div>

        {canAccessForm && (
          <div className={styles.actionsHeader}>
            <Button
              variant="contained"
              className={styles_button.buttonSquarePrimary}
              onClick={openCreateForm}
            >
              Nova Ação
            </Button>
          </div>
        )}

        {msgSuccess && (
          <div className={styles_table.message}>
            <Alert severity="success">{msgSuccess}</Alert>
          </div>
        )}

        {msgError && (
          <div className={styles_table.message}>
            <Alert severity="error">{msgError}</Alert>
          </div>
        )}

        <div className={`${styles_grid.grid12} ${styles_general.mb4}`}>
          <div className={styles_grid.inputGroup4}>
            <label className={styles_input.titleInput}>Módulo</label>
            <SelectField
              label=""
              value={moduleFilter || "Todos"}
              onChange={(value) => setModuleFilter(value === "Todos" ? "" : value)}
              options={moduleOptions}
              disableTyping
              disableClearable
            />
          </div>
        </div>

        <TableContainer className={styles_table.tableContainer}>
          {loading ? (
            <CircularProgress />
          ) : (
            <Table size="small">
              <TableHead className={styles_table.tableHead}>
                <TableRow>
                  <TableCell padding="none" className={styles_table.tableHeadRow}>Módulo</TableCell>
                  <TableCell padding="none" className={styles_table.tableHeadRow}>Nome</TableCell>
                  <TableCell padding="none" className={styles_table.tableHeadRow}>Chave</TableCell>
                  <TableCell padding="none" className={styles_table.tableHeadRow}>Ação</TableCell>
                  <TableCell padding="none" className={styles_table.tableHeadRow}>Escopo</TableCell>
                  <TableCell padding="none" className={styles_table.tableHeadRow} align="center">Status</TableCell>
                  <TableCell padding="none" className={styles_table.tableHeadRow} align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {permissionActions.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" className={styles_table.textXs}>
                      Nenhum registro encontrado
                    </TableCell>
                  </TableRow>
                )}

                {permissionActions.map((permissionAction, index) => (
                  <TableRow
                    key={permissionAction.id}
                    className={`${styles_table.tableRow} ${index % 2 === 0 ? styles_table.tableRowEven : styles_table.tableRowOdd}`}
                  >
                    <TableCell padding="none" className={styles_table.textXs}>{getModuleName(permissionAction)}</TableCell>
                    <TableCell padding="none" className={styles_table.textXs}>{permissionAction.label}</TableCell>
                    <TableCell padding="none" className={styles_table.textXs}>{permissionAction.key}</TableCell>
                    <TableCell padding="none" className={styles_table.textXs}>
                      {getActionLabel(permissionAction.action) || permissionAction.action}
                    </TableCell>
                    <TableCell padding="none" className={styles_table.textXs}>
                      {permissionAction.scope
                        ? getScopeLabel(permissionAction.scope) || permissionAction.scope
                        : "-"}
                    </TableCell>
                    <TableCell padding="none" align="center" className={styles_table.textXs}>
                      {permissionAction.active ? "Ativa" : "Inativa"}
                    </TableCell>
                    <TableCell padding="none">
                      <div className={styles_table.blockActions}>
                        {canAccessForm && (
                          <IconButton size="small" color="primary" onClick={() => openEditForm(permissionAction)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setSelectedPermissionAction(permissionAction);
                            setOpenDelete(true);
                          }}
                        >
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </CardContent>

      <Dialog open={openForm} onClose={loadingSave ? undefined : closeForm} maxWidth="md" fullWidth>
        <DialogTitle className={styles.title}>
          {selectedPermissionAction ? "Editar Ação" : "Nova Ação"}
        </DialogTitle>
        <DialogContent>
          <div className={`${styles_grid.grid12} ${styles_general.mt4}`}>
            <div className={styles_grid.inputGroup4}>
              <label className={styles_input.titleInput}>Módulo</label>
              <SelectField
                label=""
                value={form.moduleName}
                onChange={(value) =>
                  setForm((prev) => ({
                    ...prev,
                    moduleName: value,
                    key: buildPermissionKey(
                      value,
                      prev.action,
                      prev.scope,
                      prev.customAction,
                    ),
                  }))
                }
                options={formModuleOptions}
                disableTyping
                disableClearable
              />
            </div>

            <div className={styles_grid.inputGroup4}>
              <label className={styles_input.titleInput}>Ação</label>
              <SelectField
                label=""
                value={getActionLabel(form.action)}
                onChange={(value) => {
                  const nextAction =
                    ACTION_OPTIONS.find((option) => option.label === value)?.value || "";
                  setForm((prev) => {
                    const nextScope = actionSupportsScope(nextAction) ? prev.scope : "";
                    return {
                      ...prev,
                      action: nextAction,
                      scope: nextScope,
                      key: buildPermissionKey(
                        prev.moduleName,
                        nextAction,
                        nextScope,
                        prev.customAction,
                      ),
                    };
                  });
                }}
                options={ACTION_OPTIONS.map((option) => ({ nome: option.label }))}
                disableTyping
                disableClearable
              />
            </div>

            <div className={styles_grid.inputGroup4}>
              <label className={styles_input.titleInput}>Escopo</label>
              <SelectField
                label=""
                value={getScopeLabel(form.scope)}
                onChange={(value) => {
                  const nextScope =
                    SCOPE_OPTIONS.find((option) => option.label === value)?.value || "";
                  setForm((prev) => ({
                    ...prev,
                    scope: nextScope,
                    key: buildPermissionKey(
                      prev.moduleName,
                      prev.action,
                      nextScope,
                      prev.customAction,
                    ),
                  }));
                }}
                options={SCOPE_OPTIONS.map((option) => ({ nome: option.label }))}
                disableTyping
                disableClearable
                disabled={!actionSupportsScope(form.action)}
              />
            </div>

            {form.action === "other" && (
              <div className={styles_grid.inputGroup6}>
                <label className={styles_input.titleInput}>Action personalizada</label>
                <TextField
                  size="small"
                  variant="standard"
                  fullWidth
                  value={form.customAction}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      customAction: e.target.value,
                      key: buildPermissionKey(
                        prev.moduleName,
                        prev.action,
                        prev.scope,
                        e.target.value,
                      ),
                    }))
                  }
                  inputProps={{ maxLength: 100 }}
                  InputProps={{ className: styles_input.input, disableUnderline: true }}
                />
              </div>
            )}

            <div className={styles_grid.inputGroup6}>
              <label className={styles_input.titleInput}>Chave</label>
              <TextField
                size="small"
                variant="standard"
                fullWidth
                value={form.key}
                inputProps={{ maxLength: 150 }}
                InputProps={{
                  className: styles_input.input,
                  disableUnderline: true,
                  readOnly: true,
                }}
              />
            </div>

            <div className={styles_grid.inputGroup6}>
              <label className={styles_input.titleInput}>Nome</label>
              <TextField
                size="small"
                variant="standard"
                fullWidth
                value={form.label}
                onChange={(e) => setForm((prev) => ({ ...prev, label: e.target.value }))}
                inputProps={{ maxLength: 150 }}
                InputProps={{ className: styles_input.input, disableUnderline: true }}
              />
            </div>

            <div className={styles_grid.inputGroup10}>
              <label className={styles_input.titleInput}>Descrição</label>
              <TextField
                size="small"
                variant="standard"
                fullWidth
                multiline
                minRows={3}
                value={form.description}
                onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                inputProps={{ maxLength: 255 }}
                InputProps={{ className: styles_input.input, disableUnderline: true }}
              />
            </div>

            <div className={`${styles_grid.inputGroup2} ${styles_grid.itemsEnd}`}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.active}
                    onChange={(_, checked) => setForm((prev) => ({ ...prev, active: checked }))}
                  />
                }
                label={form.active ? "Ativa" : "Inativa"}
              />
            </div>
          </div>
        </DialogContent>
        <DialogActions className={styles_grid.displayFlexBetween}>
          <Button
            variant="contained"
            onClick={closeForm}
            className={styles_button.buttonSquareSecondary}
            disabled={loadingSave}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            className={`${styles_button.buttonSquarePrimary} ${loadingSave ? styles_button.buttonOpacity : ""}`}
            disabled={loadingSave}
          >
            {loadingSave ? "Salvando..." : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={openDelete}
        loading={loadingDelete}
        onClose={() => setOpenDelete(false)}
        onConfirm={handleDelete}
        title="Excluir Ação"
        message={`Deseja realmente excluir a ação "${selectedPermissionAction?.label || ""}"?`}
        titleLoadButton="Excluindo..."
        titleButtonConfirm="Excluir"
      />
    </Card>
  );
};

export default GestaoAcoesPermissao;
